"use client";
import {
  FC,
  HTMLProps,
  CSSProperties,
  forwardRef,
  useEffect,
  useId,
  useRef,
  useState,
  JSX,
} from "react";
import { createPortal } from "react-dom";

// libs
import classNames from "classnames";

// components
import { Error, ShowWhen } from "@/components/common";

// assets
import { ArrowDown, InfoIcon } from "@/components/assets/icons";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { Tooltip } from "react-tooltip";
import { useTheme } from "@/context/Theme.context";

export type OptionType = {
  id: any;
  mainText: string;
  secondaryText?: string;
  img?: string;
  icon?: JSX.Element;
};

type InputProps = {
  id?: string;
  label?: string;
  name?: string;
  error?: any;
  selectedValue?: OptionType;
  required?: boolean;
  className?: string;
  wrapperClassName?: string;
  containerClassName?: string;
  labelClassName?: string;
  disabled?: boolean;
  success?: boolean;
  iconFit?: "cover" | "contain" | "scale-down";
  mainIcon?: JSX.Element;
  handleTouch?: () => void;
  children: JSX.Element;
  translation?: boolean;
  infoContent?: any;
  onInfoIconClick?: () => void;
  isTooltipOpen?: boolean;
} & HTMLProps<HTMLInputElement>;

const Select: FC<InputProps> = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      id,
      label = "",
      selectedValue,
      name,
      maxLength,
      className,
      wrapperClassName,
      labelClassName,
      containerClassName,
      disabled,
      readOnly = true,
      onChange,
      iconFit = "cover",
      error,
      success,
      mainIcon,
      handleTouch,
      children,
      translation = true,
      infoContent,
      onInfoIconClick,
      isTooltipOpen = false,
    },
    ref,
  ) => {
    const [openDropdown, setOpenDropdown] = useState<boolean>(false);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const theme = useTheme();
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [dropdownStyle, setDropdownStyle] = useState<CSSProperties>({});
    const [isMounted, setIsMounted] = useState(false);
    const selectId = useId();
    const [parentDropdownId, setParentDropdownId] = useState<string | null>(
      null,
    );

    useEffect(() => {
      setIsMounted(true);
    }, []);

    useEffect(() => {
      if (wrapperRef.current) {
        const parentPortal = wrapperRef.current.closest(
          "[data-select-dropdown]",
        ) as HTMLElement | null;
        setParentDropdownId(parentPortal?.dataset.selectDropdown ?? null);
      }
    }, []);

    const isClickInOwnOrDescendantPortal = (target: Element): boolean => {
      const portal = target.closest(
        "[data-select-dropdown]",
      ) as HTMLElement | null;
      if (!portal) return false;
      if (portal.dataset.selectDropdown === selectId) return true;
      let pid = portal.dataset.selectParent;
      while (pid) {
        if (pid === selectId) return true;
        const p = document.querySelector(
          `[data-select-dropdown="${CSS.escape(pid)}"]`,
        ) as HTMLElement | null;
        pid = p?.dataset.selectParent;
      }
      return false;
    };

    const computePosition = () => {
      if (wrapperRef.current) {
        const rect = wrapperRef.current.getBoundingClientRect();
        const dropdownMaxHeight = 305;
        const gap = 10;
        const spaceBelow = window.innerHeight - rect.bottom;
        if (spaceBelow < dropdownMaxHeight + gap) {
          setDropdownStyle({
            position: "fixed",
            bottom: window.innerHeight - rect.top + gap,
            left: rect.left,
            width: rect.width,
            zIndex: 9999,
          });
        } else {
          setDropdownStyle({
            position: "fixed",
            top: rect.bottom + gap,
            left: rect.left,
            width: rect.width,
            zIndex: 9999,
          });
        }
      }
    };

    const t = useTranslations();
    const [touched, setTouched] = useState(false);

    const handleClickOutside = (event) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target) &&
        (!dropdownRef.current || !dropdownRef.current.contains(event.target)) &&
        !isClickInOwnOrDescendantPortal(event.target)
      ) {
        setOpenDropdown(false);
        if (touched && typeof handleTouch === "function") {
          handleTouch();
        }
      }
    };
    useEffect(() => setOpenDropdown(false), [selectedValue]);

    useEffect(() => {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [touched]);

    useEffect(() => {
      if (!openDropdown) return;
      const handleScrollOrResize = (event?: Event) => {
        if (
          event?.type === "scroll" &&
          wrapperRef.current &&
          event.target instanceof Element &&
          event.target.contains(wrapperRef.current)
        ) {
          const wrapperRect = wrapperRef.current.getBoundingClientRect();
          const containerRect = event.target.getBoundingClientRect();
          const isOutside =
            wrapperRect.bottom < containerRect.top ||
            wrapperRect.top > containerRect.bottom ||
            wrapperRect.right < containerRect.left ||
            wrapperRect.left > containerRect.right;
          if (isOutside) {
            setOpenDropdown(false);
            return;
          }
        }
        computePosition();
      };
      window.addEventListener("scroll", handleScrollOrResize, true);
      window.addEventListener("resize", handleScrollOrResize);
      return () => {
        window.removeEventListener("scroll", handleScrollOrResize, true);
        window.removeEventListener("resize", handleScrollOrResize);
      };
    }, [openDropdown]);

    const inputClasses = classNames(
      "w-full",
      "input-field",
      { capitalize: !onChange },
      "pt-4",
      "bg-transparent",
      "h-full",
      `${selectedValue?.img ? "pl-[10px] pr-8" : "px-8"}`,
      "leading-tight",
      "text-base",
      "font-normal",
      "break-all",
      "focus:outline-none",
      "active:outline-none",
      className,
      {
        // error
        "border-red-600": !!error,
        // active
        "border border-transparent": !error,
        // disabled
        "disabled:bg-zinc-100 disabled:text-zinc-400 disabled:border-none":
          !error,
        // success
        "border-emerald-600": success,
      },
    );

    const displayInputValue = () => {
      if (selectedValue?.mainText && translation) {
        return t(selectedValue?.mainText);
      }
      return selectedValue?.mainText || "";
    };

    return (
      <div
        className={classNames("relative w-full", containerClassName)}
        ref={wrapperRef}
        onClick={() => {
          setTouched(true);
        }}
      >
        <div
          className={classNames(
            "input-wrapper relative flex h-[62px] w-full items-center rounded-[2.25rem] border bg-gray-100",
            wrapperClassName,
            {
              "border-transparent active:border-[#cfe0e2]": !error,
              "border-red-600": !!error,
            },
          )}
        >
          <ShowWhen
            condition={
              !!selectedValue?.img || !!selectedValue?.icon || !!mainIcon
            }
          >
            <div className="ml-[8px] flex h-[46px] min-w-[46px] items-center justify-center overflow-hidden rounded-full">
              <ShowWhen condition={!!selectedValue?.img}>
                <Image
                  src={selectedValue?.img}
                  alt={selectedValue?.mainText || " "}
                  width={46}
                  height={46}
                  className={`h-full bg-white object-${iconFit} `}
                />
              </ShowWhen>
              <ShowWhen
                condition={!!selectedValue?.icon}
                elseRender={
                  <ShowWhen condition={!!mainIcon}>
                    <div
                      className={`black-color flex h-full w-full items-center justify-center bg-white object-${iconFit} `}
                    >
                      {mainIcon}
                    </div>
                  </ShowWhen>
                }
              >
                <div
                  className={`black-color flex h-full w-full items-center justify-center bg-white object-${iconFit} `}
                >
                  {selectedValue?.icon}
                </div>
              </ShowWhen>
            </div>
          </ShowWhen>

          <div className="relative flex h-full w-full flex-row-reverse items-end">
            <input
              id={id}
              ref={ref}
              className={inputClasses}
              name={name}
              disabled={disabled}
              maxLength={maxLength}
              placeholder=""
              value={displayInputValue()}
              type="text"
              readOnly={readOnly}
              onClick={() => {
                setOpenDropdown(true);
                computePosition();
              }}
              onFocus={() => {
                setOpenDropdown(true);
                computePosition();
              }}
              onChange={onChange}
            />
            <ShowWhen condition={infoContent || !!onInfoIconClick}>
              <div className="absolute right-[48px] top-[50%] translate-y-[-50%]">
                {infoContent ? (
                  <>
                    {
                      <InfoIcon
                        data-tooltip-id="my-tooltip"
                        onClick={onInfoIconClick}
                      />
                    }
                    <Tooltip
                      id="my-tooltip"
                      place={"right"}
                      className={classNames(
                        "!left-[67px] !rounded-[14px] !bg-white !p-[3px] drop-shadow",
                      )}
                      isOpen={isTooltipOpen}
                    >
                      {infoContent}
                    </Tooltip>
                  </>
                ) : (
                  <InfoIcon onClick={onInfoIconClick} />
                )}
              </div>
            </ShowWhen>
            <ArrowDown
              className={classNames(
                "transition-500 absolute right-[23px] top-[23px] transition-all",
                openDropdown && "rotate-[180deg]",
              )}
              onClick={() => {
                const next = !openDropdown;
                setOpenDropdown(next);
                if (next) computePosition();
              }}
            />

            <label
              className={classNames(
                "input-label leading-tight",
                labelClassName,
                selectedValue?.img && "!left-[10px]",
                !!error && "text-red-600 !opacity-100",
              )}
              htmlFor={id}
            >
              {label ? t(label) : ""}
            </label>
          </div>
        </div>
        {error && typeof error === "string" && <Error text={error} />}
        {isMounted &&
          createPortal(
            <div
              ref={dropdownRef}
              data-select-dropdown={selectId}
              {...(parentDropdownId
                ? { "data-select-parent": parentDropdownId }
                : {})}
              style={dropdownStyle}
              className={classNames(
                theme,
                "overflow-y-scroll rounded-[20px] bg-white shadow-xl transition-all duration-300",
                {
                  "max-h-[505px] opacity-100": openDropdown,
                  "pointer-events-none h-0 opacity-0": !openDropdown,
                },
              )}
            >
              {children}
            </div>,
            document.body,
          )}
      </div>
    );
  },
);

Select.displayName = "Select";

export { Select };
